import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaArrowRight, FaPlus, FaSave, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { BASE_URL, getToken } from '../../../../shared/utils.js/utils';

const emptyLayer = () => ({
  start_depth: '',
  end_depth: '',
  layer_description: '',
  legend_id: '',
  spt_n: '',
  rqd_perc: '',
  tcr_perc: '',
  ucs_pn: '',
});

const defaultFieldConfig = [
  { key: 'location', labelKey: 'boreholes.fields.location', defaultValue: 'الموقع' },
  { key: 'consultant', labelKey: 'boreholes.fields.consultant', defaultValue: 'الاستشاري' },
  { key: 'boring_method', labelKey: 'boreholes.fields.boringMethod', defaultValue: 'طريقة الحفر' },
  { key: 'diameter', labelKey: 'boreholes.fields.diameter', defaultValue: 'القطر' },
  { key: 'groundwater_level', labelKey: 'boreholes.fields.groundwaterLevel', defaultValue: 'منسوب المياه' },
  { key: 'ace_ref_no', labelKey: 'boreholes.fields.aceRefNo', defaultValue: 'رقم المرجع' },
  { key: 'report_date', labelKey: 'boreholes.fields.reportDate', defaultValue: 'تاريخ التقرير' },
  { key: 'total_depth', labelKey: 'boreholes.fields.totalDepth', defaultValue: 'العمق الكلي' },
];

const BoreholeDefaults = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [defaults, setDefaults] = useState({
    location: '',
    consultant: '',
    boring_method: '',
    diameter: '',
    groundwater_level: '',
    ace_ref_no: '',
    report_date: '',
    total_depth: '',
  });
  const [layers, setLayers] = useState([emptyLayer()]);
  const [legends, setLegends] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [legendPickerRowIndex, setLegendPickerRowIndex] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const token = getToken();
        const [legendsRes, projectsRes] = await Promise.all([
          axios.get(`${BASE_URL}/legends?active_only=true`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${BASE_URL}/projects?per_page=200`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setLegends(Array.isArray(legendsRes.data?.data) ? legendsRes.data.data : (Array.isArray(legendsRes.data) ? legendsRes.data : []));
        const projectsData = projectsRes?.data?.data?.data || projectsRes?.data?.data || projectsRes?.data || [];
        setProjects(Array.isArray(projectsData) ? projectsData : []);
      } catch (err) {
        toast.error(t('common.fetchError', { defaultValue: 'تعذر تحميل البيانات المطلوبة' }));
      }
    };
    run();
  }, [t]);

  const loadDefaults = async () => {
    if (!projectId) return toast.warn(t('boreholes.projectRequired', { defaultValue: 'أدخل رقم المشروع أولاً' }));
    setIsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${BASE_URL}/projects/${projectId}/borehole-defaults`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.defaults || res.data?.data?.defaults || res.data?.data || res.data || {};
      setDefaults({
        location: data.location || '',
        consultant: data.consultant || '',
        boring_method: data.boring_method || '',
        diameter: data.diameter || '',
        groundwater_level: data.groundwater_level || '',
        ace_ref_no: data.ace_ref_no || '',
        report_date: data.report_date || '',
        total_depth: data.total_depth || '',
      });
      const incomingLayers = Array.isArray(data.default_layers) ? data.default_layers : [];
      setLayers(incomingLayers.length ? incomingLayers.map((layer) => ({
        start_depth: layer.start_depth ?? '',
        end_depth: layer.end_depth ?? '',
        layer_description: layer.layer_description ?? '',
        legend_id: layer.legend_id ?? '',
        spt_n: layer.spt_n ?? '',
        rqd_perc: layer.rqd_perc ?? '',
        tcr_perc: layer.tcr_perc ?? '',
        ucs_pn: layer.ucs_pn ?? '',
      })) : [emptyLayer()]);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('boreholes.defaultsLoadError', { defaultValue: 'تعذر تحميل الإعدادات الافتراضية' }));
    } finally {
      setIsLoading(false);
    }
  };

  const saveDefaults = async () => {
    if (!projectId) return toast.warn(t('boreholes.projectRequired', { defaultValue: 'أدخل رقم المشروع أولاً' }));
    setIsLoading(true);
    try {
      const token = getToken();
      await axios.put(`${BASE_URL}/projects/${projectId}/borehole-defaults`, {
        ...defaults,
        default_layers: layers
          .filter((l) => l.start_depth !== '' || l.end_depth !== '' || l.layer_description || l.legend_id)
          .map((l) => ({
            start_depth: l.start_depth !== '' ? Number(l.start_depth) : undefined,
            end_depth: l.end_depth !== '' ? Number(l.end_depth) : undefined,
            layer_description: l.layer_description || undefined,
            legend_id: l.legend_id !== '' ? Number(l.legend_id) : undefined,
            spt_n: l.spt_n !== '' ? Number(l.spt_n) : undefined,
            rqd_perc: l.rqd_perc !== '' ? Number(l.rqd_perc) : null,
            tcr_perc: l.tcr_perc !== '' ? Number(l.tcr_perc) : null,
            ucs_pn: l.ucs_pn !== '' ? Number(l.ucs_pn) : null,
          })),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('boreholes.defaultsSaved', { defaultValue: 'تم حفظ الإعدادات الافتراضية' }));
    } catch (err) {
      toast.error(err?.response?.data?.message || t('boreholes.defaultsSaveError', { defaultValue: 'تعذر حفظ الإعدادات الافتراضية' }));
    } finally {
      setIsLoading(false);
    }
  };

  const legendImageUrl = (legend) => {
    if (!legend?.id) return null;
    // مسار التخزين المباشر (public/storage) مش متظبط على السيرفر، فبنستخدم الراوت المخصص لعرض صورة الرمز
    if (legend.image_path && /^https?:\/\//i.test(legend.image_path)) return legend.image_path;
    return `${BASE_URL}/legends/${legend.id}/image`;
  };

  const getLegendById = (legendId) => legends.find((item) => String(item.id) === String(legendId));
  const modalShiftStyle = isRTL
    ? { transform: 'translateX(-400px)' }
    : { transform: 'translateX(400px)' };

  return (
    <div style={{ padding: 24, backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button onClick={() => navigate('/boreholes/add')} style={btnSecondary}>
          {isRTL ? <FaArrowRight /> : <FaArrowLeft />} {t('boreholes.back', { defaultValue: 'رجوع' })}
        </button>
        <h2 style={{ margin: 0 }}>{t('boreholes.defaults', { defaultValue: 'الإعدادات الافتراضية للجسات' })}</h2>
        <div />
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={inputStyle}>
            <option value="">{t('boreholes.quick.selectProject', { defaultValue: 'اختر المشروع' })}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name_ar || project.name_en || project.name || `#${project.id}`}
              </option>
            ))}
          </select>
          <button type="button" onClick={loadDefaults} style={btnSecondary}>{t('common.load', { defaultValue: 'تحميل' })}</button>
        </div>

        <div style={gridStyle}>
          {defaultFieldConfig.map(({ key, labelKey, defaultValue }) => (
            <input
              key={key}
              value={defaults[key]}
              onChange={(e) => setDefaults((prev) => ({ ...prev, [key]: e.target.value }))}
              placeholder={t(labelKey, { defaultValue })}
              style={inputStyle}
            />
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <h4>{t('boreholes.sections.layers', { defaultValue: 'الطبقات' })}</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 840 }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>{t('boreholes.layers.from', { defaultValue: 'من' })}</th>
                <th style={thStyle}>{t('boreholes.layers.to', { defaultValue: 'إلى' })}</th>
                <th style={thStyle}>{t('boreholes.layers.description', { defaultValue: 'الوصف' })}</th>
                <th style={thStyle}>{t('boreholes.layers.legend', { defaultValue: 'الرمز' })}</th>
                <th style={thStyle}>SPT</th>
                <th style={thStyle}>RQD%</th>
                <th style={thStyle}>TCR%</th>
                <th style={thStyle}>UCS</th>
                <th style={thStyle}>-</th>
              </tr>
            </thead>
            <tbody>
              {layers.map((layer, idx) => (
                <tr key={idx}>
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={tdStyle}><input value={layer.start_depth} onChange={(e) => setLayers((prev) => prev.map((it, i) => i === idx ? { ...it, start_depth: e.target.value } : it))} style={inputStyle} /></td>
                  <td style={tdStyle}><input value={layer.end_depth} onChange={(e) => setLayers((prev) => prev.map((it, i) => i === idx ? { ...it, end_depth: e.target.value } : it))} style={inputStyle} /></td>
                  <td style={tdStyle}><input value={layer.layer_description} onChange={(e) => setLayers((prev) => prev.map((it, i) => i === idx ? { ...it, layer_description: e.target.value } : it))} style={inputStyle} /></td>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => setLegendPickerRowIndex(idx)}
                      style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {getLegendById(layer.legend_id) && legendImageUrl(getLegendById(layer.legend_id)) ? (
                          <img
                            src={legendImageUrl(getLegendById(layer.legend_id))}
                            alt="legend"
                            style={{ width: 26, height: 26, objectFit: 'contain', background: '#fff', borderRadius: 4 }}
                          />
                        ) : null}
                        <span>
                          {getLegendById(layer.legend_id)
                            ? (getLegendById(layer.legend_id)?.label_ar || getLegendById(layer.legend_id)?.label_en || getLegendById(layer.legend_id)?.code || `#${layer.legend_id}`)
                            : t('boreholes.selectLegend', { defaultValue: 'اختر الرمز' })}
                        </span>
                      </span>
                      <span style={{ color: '#888' }}>▼</span>
                    </button>
                  </td>
                  <td style={tdStyle}><input value={layer.spt_n} onChange={(e) => setLayers((prev) => prev.map((it, i) => i === idx ? { ...it, spt_n: e.target.value } : it))} style={inputStyle} /></td>
                  <td style={tdStyle}><input value={layer.rqd_perc} onChange={(e) => setLayers((prev) => prev.map((it, i) => i === idx ? { ...it, rqd_perc: e.target.value } : it))} style={inputStyle} /></td>
                  <td style={tdStyle}><input value={layer.tcr_perc} onChange={(e) => setLayers((prev) => prev.map((it, i) => i === idx ? { ...it, tcr_perc: e.target.value } : it))} style={inputStyle} /></td>
                  <td style={tdStyle}><input value={layer.ucs_pn} onChange={(e) => setLayers((prev) => prev.map((it, i) => i === idx ? { ...it, ucs_pn: e.target.value } : it))} style={inputStyle} /></td>
                  <td style={tdStyle}>
                    <button type="button" onClick={() => setLayers((prev) => prev.filter((_, i) => i !== idx))} style={btnDanger}><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setLayers((prev) => [...prev, emptyLayer()])} style={btnSecondary}><FaPlus /> {t('boreholes.layers.addLayer', { defaultValue: 'إضافة طبقة' })}</button>
          <button type="button" disabled={isLoading} onClick={saveDefaults} style={btnPrimary}><FaSave /> {t('common.save', { defaultValue: 'حفظ' })}</button>
        </div>
      </div>
      {legendPickerRowIndex !== null && (
        <div className="modal show d-block" style={modalOverlayStyle}>
          <div className="modal-dialog modal-lg modal-dialog-centered" style={{ ...modalDialogStyle, ...modalShiftStyle }}>
            <div className="modal-content" style={modalContentStyle}>
              <div className="modal-header" style={modalHeaderStyle}>
                <h5 className="modal-title">{t('boreholes.selectLegend', { defaultValue: 'اختر الرمز' })}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setLegendPickerRowIndex(null)} />
              </div>
              <div className="modal-body" style={modalBodyStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                  {legends.map((legend) => (
                    <button
                      key={legend.id}
                      type="button"
                      onClick={() => {
                        setLayers((prev) => prev.map((it, i) => i === legendPickerRowIndex ? { ...it, legend_id: legend.id } : it));
                        setLegendPickerRowIndex(null);
                      }}
                      style={{ background: '#14263b', border: '1px solid #333', borderRadius: 8, color: 'white', padding: 8, textAlign: 'center' }}
                    >
                      {legendImageUrl(legend) ? (
                        <img src={legendImageUrl(legend)} alt={legend.label_en || `legend-${legend.id}`} style={{ width: '100%', height: 90, objectFit: 'contain', background: '#fff', borderRadius: 6 }} />
                      ) : <div style={{ height: 90, background: '#1f2432', borderRadius: 6 }} />}
                      <div style={{ marginTop: 6, fontSize: 12 }}>{legend.label_ar || legend.label_en || legend.code || `#${legend.id}`}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardStyle = { backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 10, padding: 16, marginBottom: 16 };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 };
const inputStyle = { width: '100%', padding: '8px 10px', backgroundColor: '#14263b', border: '1px solid #333', color: 'white', borderRadius: 6 };
const thStyle = { padding: 8, border: '1px solid #333', fontSize: 12 };
const tdStyle = { padding: 6, border: '1px solid #333' };
const btnPrimary = { backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnSecondary = { backgroundColor: '#2f3644', color: 'white', border: '1px solid #3d465d', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnDanger = { backgroundColor: '#c0392b', color: 'white', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer' };
const modalOverlayStyle = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modalDialogStyle = { width: '100%', maxWidth: 900, margin: 0 };
const modalContentStyle = { backgroundColor: '#1b3350', color: 'white', border: '1px solid #333', borderRadius: 12, width: '100%', maxHeight: 'calc(100vh - 32px)', overflow: 'hidden' };
const modalHeaderStyle = { borderBottom: '1px solid #333', padding: '12px 16px' };
const modalBodyStyle = { overflowY: 'auto', maxHeight: 'calc(100vh - 180px)', padding: 16 };

export default BoreholeDefaults;
